/**
 * 模块: 竞品详情弹窗
 * 职责: 展示单个竞品的详细信息和威胁分析
 * 限制: 文件<280行, 函数<30行
 */

import React from 'react';
import { Modal, Button, Descriptions, Tag, Badge, Timeline } from 'antd';
import type { CompetitorASIN } from '../../types';
import { getThreatLevelConfig } from './constants';

interface CompetitorDetailModalProps {
  visible: boolean;
  competitor: CompetitorASIN | null;
  onClose: () => void;
}

export const CompetitorDetailModal: React.FC<CompetitorDetailModalProps> = ({
  visible,
  competitor,
  onClose,
}) => {
  if (!competitor) return null;

  const config = getThreatLevelConfig(competitor.threat_level);

  const getThreatAnalysis = () => {
    if (competitor.threat_level === 'high') {
      return (
        <ul>
          <li>该竞品频繁出现在Top3位置，具有较强的市场竞争力</li>
          <li>建议深入分析其产品特点、定价策略和营销手段</li>
          <li>考虑调整自身产品策略以应对竞争</li>
        </ul>
      );
    }
    
    if (competitor.threat_level === 'medium') {
      return (
        <ul>
          <li>该竞品偶尔出现在Top3位置，具有一定的竞争力</li>
          <li>建议持续观察其市场表现和变化趋势</li>
          <li>关注其产品更新和促销活动</li>
        </ul>
      );
    }
    
    return (
      <ul>
        <li>该竞品较少出现在Top3位置，威胁相对较小</li>
        <li>可以作为参考对象，但不需要过度关注</li>
        <li>定期检查其是否有显著变化</li>
      </ul>
    );
  };

  return (
    <Modal
      title="竞品详情"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
        <Button
          key="amazon"
          type="primary"
          href={`https://www.amazon.com/dp/${competitor.asin}`}
          target="_blank"
        >
          在亚马逊查看
        </Button>,
      ]}
      width={700}
    >
      <Descriptions column={2} bordered>
        <Descriptions.Item label="ASIN" span={2}>
          {competitor.asin}
        </Descriptions.Item>
        <Descriptions.Item label="品牌" span={2}>
          {competitor.brand || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="威胁等级">
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="出现次数">
          <Badge
            count={competitor.appearance_count}
            showZero
            style={{ backgroundColor: '#1890ff' }}
          />
        </Descriptions.Item>
        <Descriptions.Item label="首次出现">
          {competitor.first_seen}
        </Descriptions.Item>
        <Descriptions.Item label="最后出现">
          {competitor.last_seen}
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 24 }}>
        <h4>威胁分析</h4>
        <p>{config.description}</p>
        {getThreatAnalysis()}
      </div>

      {competitor.appearance_history && (
        <div style={{ marginTop: 24 }}>
          <h4>出现历史</h4>
          <Timeline>
            {competitor.appearance_history.map((item: any, index: number) => (
              <Timeline.Item key={index}>
                <p>
                  <strong>{item.date}</strong> - 在关键词 "{item.keyword}" 中排名第
                  {item.position}
                </p>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      )}
    </Modal>
  );
};

